import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Check, ShieldCheck, Truck, Star, Minus, Plus, ChevronRight, ChevronDown, Calculator, X } from "lucide-react";
import MaroonNavbar from "@/components/new/MaroonNavbar";
import MaroonFooter from "@/components/new/MaroonFooter";
import CheckoutDrawer from "@/components/new/CheckoutDrawer";
import WhatsAppButton from "@/components/new/WhatsAppButton";

const C = { maroon: "#621B21", maroonDark: "#5C0F1C", yellow: "#F5C800", beige: "#F5EDD6", white: "#FFFFFF" };

const TRIAL_NEW = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/whgfkde7_Trial%20Pack%20Ethia%20%281%29.png";
const VALUE_NEW = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/lp2gvf2w_Ethia%20Value%20Pack%20%281%29.png";
const FAMILY_NEW = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/qcz1ezz2_Ethia%20Family%20Pack%20%281%29.png";
const BACK_IMG = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/kuzbrmjm_Ethia%20Back%20of%20the%20Pack%20Image.webp";
const TRIAL_ACTION = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/cmpv1zf0_Ethia%20Trial%20Pack%20Image%203.png";
const VALUE_ACTION = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/g0tiuobp_Ethia%20Value%20Pack%20Image%202.png";
const FAMILY_ACTION = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/ooeh3ljq_Ethia%20Family%20Pack%203.png";
const LARGE_NEW = "https://customer-assets-rejwkqb3.emergentagent.net/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/4ed9b9118f0f671d_Large%20Pack%201%20pouch%20of%20300g%20Price.png";

const PUPPY_PHOTO = "https://images.unsplash.com/photo-1526660690293-bcd32dc3b123?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxwdXBweSUyMHNtYWxsJTIwZG9nJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzkwMzQyMDAxfDA&ixlib=rb-4.1.0&q=85";
const POM_PHOTO = "https://images.pexels.com/photos/9649679/pexels-photo-9649679.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const BEAGLE_PHOTO = "https://images.unsplash.com/photo-1543466835-00a7907e9de1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxiZWFnbGUlMjBkb2clMjBwb3J0cmFpdHxlbnwwfHx8fDE3OTAzNDIwMDF8MA&ixlib=rb-4.1.0&q=85";
const GSD_PHOTO = "https://images.unsplash.com/photo-1649571069618-99a265749d5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxnZXJtYW4lMjBzaGVwaGVyZCUyMGRvZyUyMHBvcnRyYWl0fGVufDB8fHx8MTc5MDM0MjAwMXww&ixlib=rb-4.1.0&q=85";

const useReveal = () => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => { const el = ref.current; if (!el) return; const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.2 }); o.observe(el); return () => o.disconnect(); }, []);
  return [ref, vis];
};

const INGREDIENTS_LIST = [
  "Cultured Chicken Cells", "Pea Protein", "Shiitake Mushroom Extract",
  "Chickpea Flour", "Pumpkin Powder", "Potato Starch",
  "Yeast Extract", "Sodium Alginate", "Vitamins and Minerals",
];

const PRODUCT_DATA = {
  "trial-pack": {
    id: "trial", name: "Trial Pack", tag: "TASTE THE FUTURE",
    tagline: "Perfect for first-time buyers — try before you commit.",
    price: 199, gstPrice: 235, weight: "160g", packets: "2 pouches of 80g each",
    images: [TRIAL_NEW, BACK_IMG, TRIAL_ACTION],
    features: [
      "Real cultivated chicken protein",
      "2 pouches × 80g each (160g total)",
      "No antibiotics, no hormones",
      "Clean label ingredients only",
      "Perfect introduction to cultivated pet food",
    ],
    messageLine1: "Not sure yet? This is the perfect place to start.",
    messageLine2: "Try the future of pet nutrition — risk free.",
  },
  "value-pack": {
    id: "value", name: "Value Pack", tag: "THE SMART CHOICE",
    tagline: "Our most popular pack — the best balance of value and nutrition.",
    price: 499, gstPrice: 589, weight: "400g", packets: "5 pouches of 80g each",
    images: [VALUE_NEW, BACK_IMG, VALUE_ACTION],
    features: [
      "Real cultivated chicken protein",
      "5 pouches × 80g each (400g total)",
      "No antibiotics, no hormones",
      "Clean label ingredients only",
      "Best value for regular feeding",
    ],
    messageLine1: "Chosen by most of our founding members.",
    messageLine2: "The smart choice for dogs who deserve better, every day.",
  },
  "family-pack": {
    id: "family", name: "Family Pack", tag: "THE FULL COMMITMENT", popular: true,
    tagline: "Maximum value — commit to the future of feeding.",
    price: 999, gstPrice: 1179, weight: "960g", packets: "12 pouches of 80g each",
    images: [FAMILY_NEW, BACK_IMG, FAMILY_ACTION],
    features: [
      "Real cultivated chicken protein",
      "12 pouches × 80g each (960g total)",
      "No antibiotics, no hormones",
      "Clean label ingredients only",
      "Best per-pouch value — save more",
    ],
    messageLine1: "For families fully committed to ethical, science-backed nutrition.",
    messageLine2: "The lowest cost per pouch.",
  },
  "large-pack": {
    id: "large", name: "Large Pack", tag: "BIGGER BITE",
    tagline: "One generous 300g pouch — perfect for a single, satisfying meal.",
    price: 237, gstPrice: 280, weight: "300g", packets: "1 pouch of 300g",
    images: [LARGE_NEW, BACK_IMG, LARGE_NEW],
    features: [
      "Real cultivated chicken protein",
      "1 large pouch × 300g",
      "No antibiotics, no hormones",
      "Clean label ingredients only",
      "Ideal for medium to large dogs",
    ],
    messageLine1: "A single, generous serving for dogs who need more.",
    messageLine2: "Perfect for medium to large breeds in one convenient pouch.",
  },
};

const TRUST_BADGES = [
  { icon: <ShieldCheck size={16} />, label: "100% Slaughter Free" },
  { icon: <Check size={16} />, label: "Lab Tested" },
  { icon: <Star size={16} />, label: "Vet Approved" },
  { icon: <Truck size={16} />, label: "Ships All India" },
];

/* ─── Lightbox ─── */
const Lightbox = ({ src, alt, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose} data-testid="lightbox">
    <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/40 transition-colors" onClick={onClose}><X size={20} color="#fff" /></button>
    <img src={src} alt={alt} className="max-w-full max-h-[90vh] object-contain rounded-xl" onClick={(e) => e.stopPropagation()} />
  </div>
);

/* ─── Subscription Section ─── */
const SubscriptionSection = () => {
  const tiers = [
    { label: "A", discount: "10%", minOrder: "₹3,000", commitment: "One-time", freeMonth: null, desc: "Purchase from the website with a minimum basket value of ₹3,000 to avail the discount." },
    { label: "B", discount: "20%", minOrder: "₹3,000 or 3 Family Packs", commitment: "4 months", freeMonth: "5th month free", desc: "Customized subscription with a 4-month commitment. Your 5th month's supply is on us." },
    { label: "C", discount: "Up to 30%", minOrder: "₹3,000 or 3 Family Packs", commitment: "5 months", freeMonth: "6th & 7th month free", desc: "Customized subscription with a minimum 5-month commitment. Your 6th and 7th month's supply is on us." },
  ];
  return (
    <section className="py-14 md:py-20" style={{ background: C.maroon }} data-testid="pdp-subscription">
      <div className="max-w-[1100px] mx-auto px-5 md:px-[52px]">
        <div className="text-center mb-4"><span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: C.yellow }}>SAVE MORE</span></div>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>Subscription Plans</h2>
        <p className="text-center text-sm mb-10" style={{ color: `${C.beige}CC` }}>The longer you commit, the more you save.</p>
        <div className="rounded-2xl p-6 md:p-8 mb-6 relative overflow-hidden" style={{ background: C.maroonDark, border: `2px solid ${C.yellow}` }}>
          <div className="absolute top-0 right-0 px-4 py-1 rounded-bl-xl text-xs font-bold" style={{ background: C.yellow, color: C.maroon }}>Best Value</div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold mb-1" style={{ color: C.yellow, fontFamily: "Fraunces, serif" }}>Annual Plan — Highest Savings</h3>
              <p className="text-sm" style={{ color: `${C.beige}CC` }}>Pay for 8 months, get 12 months of supply. 4 months complimentary based on your chosen pack size.</p>
            </div>
            <div className="text-center md:text-right flex-shrink-0">
              <div className="text-3xl font-black" style={{ color: C.yellow, fontFamily: "Fraunces, serif" }}>4 Months Free</div>
              <div className="text-xs mt-1" style={{ color: `${C.beige}99` }}>12 months commitment</div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {tiers.map((t, i) => (
            <div key={i} className="rounded-2xl p-5 md:p-6" style={{ background: C.maroonDark }}>
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: C.yellow, color: C.maroon }}>{t.label}</span>
                <span className="text-2xl font-black" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>{t.discount}</span>
              </div>
              <div className="space-y-2 text-xs mb-4" style={{ color: `${C.beige}CC` }}>
                <div className="flex justify-between"><span style={{ color: `${C.beige}80` }}>Min. Order</span><span className="font-semibold" style={{ color: C.beige }}>{t.minOrder}</span></div>
                <div className="flex justify-between"><span style={{ color: `${C.beige}80` }}>Commitment</span><span className="font-semibold" style={{ color: C.beige }}>{t.commitment}</span></div>
                {t.freeMonth && <div className="flex justify-between"><span style={{ color: `${C.beige}80` }}>Benefit</span><span className="font-bold" style={{ color: C.yellow }}>{t.freeMonth}</span></div>}
              </div>
              <p className="text-[11px] leading-relaxed" style={{ color: `${C.beige}99` }}>{t.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Feeding Pattern Section ─── */
const FeedingPatternSection = () => {
  const [showCalc, setShowCalc] = useState(false);
  const [ref, vis] = useReveal();
  const dogs = [
    { size: "Puppies", weight: "1–5 kg", pouches: "1½–5½", photo: PUPPY_PHOTO },
    { size: "Small", weight: "5–10 kg", pouches: "5½–9", photo: POM_PHOTO },
    { size: "Medium", weight: "10–25 kg", pouches: "9–18", photo: BEAGLE_PHOTO },
    { size: "Large", weight: "25–40 kg", pouches: "18–26", photo: GSD_PHOTO },
  ];
  return (
    <section ref={ref} className="py-14 md:py-20" style={{ background: C.beige }} data-testid="pdp-feeding">
      <div className={`max-w-[900px] mx-auto px-5 md:px-[52px] transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-2" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>How Much to Feed Your Dog</h2>
        <p className="text-center text-xs mb-8" style={{ color: `${C.maroon}80` }}>Based on an 80g pouch &middot; Energy density 86 kcal/100g</p>
        <div className="space-y-3 mb-6">
          {dogs.map((d, i) => (
            <div key={i} className="rounded-xl p-4 md:p-5 flex items-center justify-between shadow-sm" style={{ background: C.white }}>
              <div className="flex items-center gap-3">
                <img src={d.photo} alt={d.size} className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover flex-shrink-0" style={{ border: `2px solid ${C.yellow}` }} />
                <div>
                  <div className="font-bold text-sm" style={{ color: C.maroon }}>{d.size}</div>
                  <div className="text-xs" style={{ color: `${C.maroon}80` }}>{d.weight}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl md:text-3xl font-black" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>{d.pouches}</div>
                <div className="text-[10px]" style={{ color: `${C.maroon}80` }}>pouches/day</div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-center mb-8" style={{ color: `${C.maroon}80` }}>Adjust according to your dog's activity level.</p>
        <div className="rounded-xl overflow-hidden" style={{ background: C.white, border: `1px solid ${C.maroon}10` }}>
          <button onClick={() => setShowCalc(!showCalc)} className="w-full flex items-center justify-between px-5 py-3.5 text-left">
            <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: C.maroon }}><Calculator size={14} /> How We Calculate This</span>
            <ChevronDown size={16} className={`transition-transform duration-300 ${showCalc ? "rotate-180" : ""}`} style={{ color: C.maroon }} />
          </button>
          {showCalc && (
            <div className="px-5 pb-4 space-y-1 text-xs font-mono" style={{ color: `${C.maroon}CC` }}>
              <p>RER = 70 × body weight<sup>0.75</sup></p>
              <p>DER = 1.6 × RER</p>
              <p>Daily grams = DER ÷ 0.86</p>
              <p>Pouches/day = daily grams ÷ 80</p>
              <p className="text-[10px] mt-3 italic font-sans" style={{ color: `${C.maroon}60` }}>This chart reflects an average adult, neutered dog at maintenance. Adjust as needed.</p>
            </div>
          )}
        </div>
        <div className="mt-14">
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-3" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>How to Start Feeding with ETHIA</h3>
          <p className="text-center text-xs mb-8 max-w-xl mx-auto" style={{ color: `${C.maroon}99` }}>Introduce ETHIA gradually over 7 days by mixing it with your dog's current food:</p>
          <p className="text-xs text-center font-bold mb-8 max-w-xl mx-auto leading-relaxed" style={{ color: C.maroon }}>For best results, mix ETHIA with your dog's regular meal (rice or an equivalent) rather than feeding it alone. The number of pouches per day is adjusted based on how much of your dog's daily energy ETHIA provides.</p>
          <div className="grid grid-cols-4 gap-2 md:gap-4 mb-6">
            {[["Day 1","25%","75%"],["Day 3","50%","50%"],["Day 5","75%","25%"],["Day 7","100%","0%"]].map(([day,ethia,curr],i) => (
              <div key={i} className="rounded-xl p-3 md:p-4 text-center shadow-sm" style={{ background: C.white }}>
                <div className="text-xs font-bold mb-2" style={{ color: C.maroon }}>{day}</div>
                <div className="text-sm font-bold mb-0.5" style={{ color: C.maroon }}>{ethia} ETHIA</div>
                {curr !== "0%" ? <div className="text-[10px]" style={{ color: `${C.maroon}80` }}>{curr} current food</div> : <div className="text-[10px] font-bold" style={{ color: C.yellow }}>Full ETHIA!</div>}
              </div>
            ))}
          </div>
          <p className="text-xs text-center italic" style={{ color: "#E57373" }}>If loose stools or refusal to eat occurs, slow the transition down and consult your veterinarian if symptoms persist.</p>
        </div>
      </div>
    </section>
  );
};

/* ─── FAQ ─── */
const FAQS = [
  { q: "What is cultivated pet food?", a: "Cultivated pet food is made using cultivated chicken cells grown in a controlled environment, without raising or slaughtering animals. ETHIA uses cultivated chicken cells blended with plant and algal proteins in a sterile bioreactor to create wet food protein for your adorable pet." },
  { q: "Is ETHIA safe for my dog?", a: "Absolutely! ETHIA is made with real chicken cells and is vet-approved. Our formula is lab-tested, antibiotic-free, and contains no harmful additives or byproducts." },
  { q: "How does cultivated meat compare nutritionally?", a: "ETHIA is formulated to provide dogs with key nutrients they need as part of a balanced diet. It uses cultivated chicken protein and is designed with canine nutritional requirements in mind." },
  { q: "Why is ETHIA better than regular pet food?", a: "Most conventional pet food contains meat derivatives, unknown sourcing, artificial preservatives, and fillers. ETHIA has a clean, readable ingredient list. No slaughter. No mystery. No compromise." },
  { q: "How do I transition my dog to ETHIA?", a: "Begin with small portions and gradually increase to the recommended daily amount over 7 days. Feed according to your dog's size, age, and activity level." },
];

const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section className="py-16 md:py-24" style={{ background: C.maroonDark }} data-testid="pdp-faq">
      <div className="max-w-[680px] mx-auto px-6">
        <div className="text-center mb-4"><span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: C.yellow }}>FAQS</span></div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-12" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>Your Questions Answered</h2>
        {FAQS.map((faq, i) => (
          <div key={i} style={{ borderBottom: `1px solid ${C.maroon}` }}>
            <button onClick={() => setOpenIdx(openIdx === i ? null : i)} className="w-full py-5 flex items-center justify-between text-left gap-4">
              <span className="font-medium text-sm md:text-base" style={{ color: C.beige }}>{faq.q}</span>
              <ChevronDown size={20} className={`flex-shrink-0 transition-transform duration-300 ${openIdx === i ? "rotate-180" : ""}`} style={{ color: C.yellow }} />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${openIdx === i ? "max-h-[500px] pb-5" : "max-h-0"}`}>
              <p className="text-xs md:text-sm leading-relaxed pr-8" style={{ color: `${C.beige}BB` }}>{faq.a}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ─── Page ─── */
const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = PRODUCT_DATA[slug];
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoom, setZoom] = useState({ active: false, x: 0, y: 0 });
  const isTouchRef = useRef(false);

  useEffect(() => {
    isTouchRef.current = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  }, []);

  const handleZoomMove = (e) => {
    if (isTouchRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoom({ active: true, x, y });
  };

  useEffect(() => {
    if (!product) { navigate("/products", { replace: true }); return; }
    document.title = `ETHIA | ${product.name}`;
    window.scrollTo(0, 0);
  }, [product, navigate, slug]);

  if (!product) return null;

  const handleOrder = () => {
    setIsCheckoutOpen(true);
    if (typeof window.fbq === "function") window.fbq("track", "InitiateCheckout");
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="pdp-page">
      <MaroonNavbar onOrder={handleOrder} />

      {/* Breadcrumb */}
      <div className="pt-24 md:pt-28" style={{ background: C.beige }}>
        <div className="max-w-[1100px] mx-auto px-5 md:px-[52px] py-4">
          <nav className="flex items-center gap-1.5 text-xs" data-testid="pdp-breadcrumb">
            <Link to="/" className="hover:underline font-medium" style={{ color: C.maroon }}>Home</Link>
            <ChevronRight size={12} style={{ color: `${C.maroon}80` }} />
            <Link to="/products" className="hover:underline font-medium" style={{ color: C.maroon }}>Products</Link>
            <ChevronRight size={12} style={{ color: `${C.maroon}80` }} />
            <span className="font-bold" style={{ color: C.maroon }}>{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product Section */}
      <section className="pb-16 md:pb-24" style={{ background: C.beige }} data-testid="pdp-product-section">
        <div className="max-w-[1100px] mx-auto px-5 md:px-[52px]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">
            {/* Left — Gallery */}
            <div className="flex flex-col-reverse lg:flex-row gap-3 lg:items-start" data-testid="pdp-gallery">
              <div className="flex lg:flex-col gap-2 justify-center lg:justify-start">
                {product.images.map((img, i) => (
                  <button key={i} onClick={() => { setActiveImg(i); setZoom({ active: false, x: 0, y: 0 }); }}
                    className={`w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${activeImg === i ? "border-[#e6b93d] shadow-md" : "border-transparent opacity-60 hover:opacity-100"}`}
                    data-testid={`pdp-thumb-${i}`}>
                    <img src={img} alt={`View ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
              <div
                className="flex-1 rounded-2xl overflow-hidden shadow-lg relative"
                style={{ aspectRatio: "1/1", cursor: zoom.active ? "zoom-in" : "zoom-in" }}
                onMouseEnter={() => { if (!isTouchRef.current) setZoom(z => ({ ...z, active: true })); }}
                onMouseMove={handleZoomMove}
                onMouseLeave={() => setZoom({ active: false, x: 0, y: 0 })}
                onClick={() => { if (isTouchRef.current) setLightboxOpen(true); }}
                data-testid="pdp-main-img"
              >
                <img
                  src={product.images[activeImg]}
                  alt={product.name}
                  className={`w-full h-full object-cover rounded-2xl transition-opacity duration-200 ${zoom.active ? "opacity-0" : "opacity-100"}`}
                />
                {zoom.active && (
                  <div
                    className="absolute inset-0 rounded-2xl"
                    style={{
                      backgroundImage: `url(${product.images[activeImg]})`,
                      backgroundSize: "250%",
                      backgroundPosition: `${zoom.x}% ${zoom.y}%`,
                      backgroundRepeat: "no-repeat",
                    }}
                  />
                )}
              </div>
            </div>

            {/* Right — Details */}
            <div className="py-2 lg:py-6">
              {product.popular && (
                <span className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase mb-3" style={{ background: C.yellow, color: C.maroon }}>Most Popular</span>
              )}
              <span className="block text-xs font-bold tracking-[0.15em] uppercase mb-2" style={{ color: C.maroon }}>{product.tag}</span>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }} data-testid="pdp-title">{product.name}</h1>
              <p className="text-sm font-medium mb-6" style={{ color: C.maroon }}>{product.tagline}</p>

              {/* Pricing */}
              <div className="mb-6 pb-6" style={{ borderBottom: `1px solid ${C.maroon}20` }}>
                <div className="flex items-baseline gap-3">
                  <span className="text-4xl font-black" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }} data-testid="pdp-price">₹{product.price}</span>
                  <span className="text-sm font-semibold" style={{ color: C.maroon }}>₹{product.gstPrice} incl. GST</span>
                </div>
              </div>

              {/* Pack details */}
              <div className="mb-6">
                <div className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: C.maroon }}>Pack Details</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg px-3 py-2.5" style={{ background: `${C.maroon}10` }}>
                    <div className="text-[10px] uppercase font-semibold" style={{ color: C.maroon }}>Weight</div>
                    <div className="text-sm font-bold" style={{ color: C.maroon }}>{product.weight}</div>
                  </div>
                  <div className="rounded-lg px-3 py-2.5" style={{ background: `${C.maroon}10` }}>
                    <div className="text-[10px] uppercase font-semibold" style={{ color: C.maroon }}>Contents</div>
                    <div className="text-sm font-bold" style={{ color: C.maroon }}>{product.packets}</div>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="mb-6">
                {product.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2.5 py-1.5">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${C.yellow}30` }}>
                      <Check size={10} style={{ color: C.maroon }} strokeWidth={3} />
                    </div>
                    <span className="text-sm font-medium" style={{ color: C.maroon }}>{f}</span>
                  </div>
                ))}
              </div>

              {/* Ingredients */}
              <div className="mb-6">
                <div className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: C.maroon }}>Ingredients</div>
                <div className="flex flex-wrap gap-1.5">
                  {INGREDIENTS_LIST.map((ing, i) => (
                    <span key={i} className="px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: `${C.maroon}10`, color: C.maroon }}>{ing}</span>
                  ))}
                </div>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-2 mb-6">
                {TRUST_BADGES.map((b, i) => (
                  <span key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold" style={{ background: `${C.maroon}10`, color: C.maroon }} data-testid={`pdp-badge-${i}`}>
                    <span style={{ color: C.yellow }}>{b.icon}</span>{b.label}
                  </span>
                ))}
              </div>

              {/* Messaging — two lines */}
              <div className="rounded-xl px-4 py-3 mb-6" style={{ background: `${C.yellow}15`, border: `1px solid ${C.yellow}30` }}>
                <p className="text-xs leading-relaxed font-medium" style={{ color: C.maroon }}>
                  {product.messageLine1}<br />{product.messageLine2}
                </p>
              </div>

              {/* Stock */}
              <div className="flex items-center gap-2 mb-5">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-xs font-bold" style={{ color: C.maroon }}>In Stock — Ships within 2-3 business days</span>
              </div>

              {/* Quantity + Order */}
              <div className="flex items-center gap-4">
                <div className="flex items-center rounded-full overflow-hidden" style={{ border: `1.5px solid ${C.maroon}30` }}>
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2.5 hover:bg-black/5 transition-colors" data-testid="pdp-qty-minus"><Minus size={16} style={{ color: C.maroon }} /></button>
                  <span className="px-4 text-sm font-bold" style={{ color: C.maroon }} data-testid="pdp-qty">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="px-3 py-2.5 hover:bg-black/5 transition-colors" data-testid="pdp-qty-plus"><Plus size={16} style={{ color: C.maroon }} /></button>
                </div>
                <button onClick={handleOrder} className="flex-1 font-bold py-3.5 rounded-full text-sm transition-all hover:scale-[1.02] shadow-lg" style={{ background: C.yellow, color: C.maroon }} data-testid="pdp-order-btn">
                  Order Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Sections */}
      <SubscriptionSection />
      <FeedingPatternSection />
      <FAQSection />

      <MaroonFooter />
      <WhatsAppButton />
      <CheckoutDrawer isOpen={isCheckoutOpen} onClose={() => setIsCheckoutOpen(false)} preselectedSku={product.id} />
      {lightboxOpen && <Lightbox src={product.images[activeImg]} alt={product.name} onClose={() => setLightboxOpen(false)} />}
    </div>
  );
};

export default ProductDetailPage;
